import type { HubCoverageSubmission } from "@itaas/insurance-contract";
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { IAM_TENANT_SLUG } from "@/core/config";
import { applyCoverageCaseReviewSideEffects } from "./coverage-case-review-effects";
import { getCoverageCase, listCoverageCases, reviewCoverageCase } from "./coverage-case-store";
import { fetchSubmissionFromHubApi } from "./hub-api-client";

const POLL_MS = 20_000;

function hubStatusToCoverageStatus(
  status: HubCoverageSubmission["status"],
): "approved" | "rejected" | "partial" | null {
  if (status === "approved" || status === "rejected" || status === "partial") return status;
  return null;
}

/** Synchronise un dossier local depuis le hub assureur (par hubSubmissionId). */
export async function syncCoverageCaseFromHub(caseId: string): Promise<boolean> {
  const coverageCase = getCoverageCase(caseId);
  if (!coverageCase?.hubSubmissionId || coverageCase.status !== "pending") {
    return false;
  }

  const hubSubmission = await fetchSubmissionFromHubApi(coverageCase.hubSubmissionId);
  if (!hubSubmission) return false;

  const mapped = hubStatusToCoverageStatus(hubSubmission.status);
  if (!mapped) return false;

  const updated = reviewCoverageCase(
    caseId,
    mapped,
    hubSubmission.reviewedBy || "Hub assureur",
    hubSubmission.rejectionReason,
  );
  if (!updated) return false;

  window.dispatchEvent(
    new CustomEvent("hospitol:hub-coverage-synced", { detail: { caseId, status: mapped } }),
  );
  return true;
}

/** Polling des dossiers en attente soumis au hub. */
export async function syncPendingHubCoverageCases(): Promise<number> {
  const tenantSlug = loadStoredSession()?.tenant?.slug ?? IAM_TENANT_SLUG;
  const pending = listCoverageCases({ status: "pending" }).filter(
    (c) => c.hubSubmissionId,
  );
  let synced = 0;
  for (const coverageCase of pending) {
    const ok = await syncCoverageCaseFromHub(coverageCase.id);
    if (ok) synced += 1;
  }
  void tenantSlug;
  return synced;
}

function startHubCoveragePolling(): void {
  void syncPendingHubCoverageCases();
  window.setInterval(() => {
    void syncPendingHubCoverageCases();
  }, POLL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void syncPendingHubCoverageCases();
    }
  });
}

startHubCoveragePolling();
